import * as React from 'react';
import { Box } from '@mui/material';
import { KlaraStatus } from '../../../model/klara';

type Tone = 'neutral' | 'info' | 'warning' | 'success' | 'danger' | 'muted';

/**
 * What each status says about who is waiting, from the design's own note:
 * blue = Klara is working, amber = we are waiting on the user, green = done,
 * grey = outside the flow.
 *
 * The tone carries the visual, the label carries the meaning — so the wording
 * stays whatever `klara.statuses.*` says. Which of the two vocabularies wins is
 * decision 4.1 and is not settled here.
 */
const TONES: Record<string, Tone> = {
  draft: 'neutral',
  brief_requested: 'info',
  brief_ready: 'info',
  evaluation_requested: 'info',
  evaluation_ready: 'warning',
  generation_requested: 'info',
  generating: 'info',
  completed: 'success',
  failed: 'danger',
  // Not drawn at all. Grey is the design's own "mimo tok".
  cancelled: 'muted',
};

const COLORS: Record<Tone, { background: string; color: string; border?: string }> = {
  neutral: { background: '#efefef', color: '#666666' },
  info: { background: '#e7f0fa', color: '#2c6da8' },
  warning: { background: '#fff4e0', color: '#b9791a' },
  success: { background: '#eef4e4', color: '#6f9f1e' },
  danger: { background: '#fbe6e4', color: '#e2574c' },
  muted: { background: 'transparent', color: '#949494', border: '#d9d9d9' },
};

/**
 * The status pill from the design system.
 *
 * The list frame itself (`435:2683`) still prints the status as plain text —
 * it predates this component, whose node ids are newer, and the same
 * "the newer frame wins" reading settles the topbar in §5.1 of the review.
 */
export function StatusBadge({ status, label }: { status: KlaraStatus; label: string }) {
  const tone = TONES[status] ?? 'neutral';
  const { background, color, border } = COLORS[tone];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '19px',
        padding: '3px 9px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        backgroundColor: background,
        color,
        border: border ? `1px solid ${border}` : undefined,
      }}
    >
      {label}
    </Box>
  );
}
